import { memo, useRef, useEffect, useCallback } from 'react'
import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts'

// ——— helpers estables fuera del componente ———
const NoLegend = () => null
const isYYYYMM = s => typeof s === 'string' && /^\d{4}-\d{2}$/.test(s)
const COLOR_MUTED = 'rgb(var(--muted))'
const COLOR_GRID = 'rgb(var(--border))'
const COLOR_SUCCESS = 'rgb(var(--success))'
const COLOR_DANGER = 'rgb(var(--danger))'
const MONTHS_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']
const dataFingerprint = (arr = []) => {
  if (!Array.isArray(arr) || arr.length === 0) return '0'
  const last = arr[arr.length - 1] || {}
  // Largo + último punto (nombre/valores) suele ser suficiente para detectar cambios útiles
  return `${arr.length}|${last.name ?? ''}|${last.Abono ?? 0}|${last.Cargo ?? 0}`
}

const SimpleBarChart = ({ data, onSelectMonth, selectedMonth }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  // 1) Mantener siempre la versión más nueva del callback sin forzar re-render
  const onSelectRef = useRef(onSelectMonth)
  useEffect(() => { onSelectRef.current = onSelectMonth }, [onSelectMonth])

  // 2) Congelar la data si el "fingerprint" no cambia (evita updates innecesarios del chart)
  const dataRef = useRef(data)
  const fpRef = useRef(dataFingerprint(data))
  useEffect(() => {
    const fp = dataFingerprint(data)
    if (fp !== fpRef.current) {
      fpRef.current = fp
      dataRef.current = data
    }
    // si el fingerprint es igual, mantenemos la referencia anterior
  }, [data])

  const handleClick = useCallback((e) => {
    if (e && e.activePayload && e.activePayload.length) {
      const clickedMonth = e.activePayload[0].payload.name
      if (clickedMonth !== selectedMonth) {
        onSelectRef.current?.({ name: clickedMonth })
      }
    }
  }, [selectedMonth]) // solo depende del valor primitivo seleccionado

  const formatCLP = n => Number(n ?? 0).toLocaleString('es-CL')
  const pointCount = dataRef.current?.length || 0
  const shouldUseShortMonthLabel = isMobile || pointCount >= 6

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null

  const raw = payload[0].payload
  let month = raw.name
  if (isYYYYMM(raw.name)) {
    const monthIndex = parseInt(raw.name.slice(5, 7), 10) - 1
    month = MONTHS_ES[monthIndex]
  }

  const abono = Number(raw.Abono || 0)
  const cargo = Number(raw.Cargo || 0)

  return (
    <div className="bg-surface rounded-2xl shadow-card border border-border px-4 py-3 text-text">
      
    <div className="font-extrabold text-lg mb-1"> {month} </div>
      {/* Abono */}
      <div className="flex items-center gap-2">
        <span
          className="inline-block w-2.5 h-2.5 rounded-full"
          style={{ backgroundColor: COLOR_SUCCESS }}
        />
        <span className="font-semibold" style={{ color: COLOR_SUCCESS }}>
          Abono:
        </span>
        <span className="ml-1 font-medium">
          ${formatCLP(abono)}
        </span>
      </div>

      {/* Cargo */}
      <div className="flex items-center gap-2 mt-1">
        <span
          className="inline-block w-2.5 h-2.5 rounded-full"
          style={{ backgroundColor: COLOR_DANGER }}
        />
        <span className="font-semibold" style={{ color: COLOR_DANGER }}>
          Cargo:
        </span>
        <span className="ml-1 font-medium">
          ${formatCLP(cargo)}
        </span>
      </div>

    </div>
  )
}


  return (
    // Usa height del contenedor padre fijo (o aquí height="100%" con contenedor de 300px)
    <ResponsiveContainer width="100%" height={isMobile ? 240 : 300} debounce={120}>
      <LineChart
        data={dataRef.current}
        margin={isMobile ? { top: 12, right: 8, left: 0, bottom: 0 } : { top: 20, right: 15, left: 3, bottom: 3 }}
        onClick={handleClick}
      >
        <CartesianGrid strokeDasharray="1 1" vertical={false} stroke={COLOR_GRID} />
        <XAxis
          dataKey="name"
          tick={{ fontSize: isMobile ? 11 : pointCount >= 8 ? 11 : 13, fill: COLOR_MUTED }}
          axisLine={false}
          tickLine={false}
          dy={8}
          interval={0}
          minTickGap={isMobile ? 8 : 4}
          tickFormatter={(value) => {
            if (isYYYYMM(value)) {
              const [year, month] = value.split('-')
              const monthIndex = parseInt(month, 10) - 1
              const monthName = new Intl.DateTimeFormat('es-ES', {
                month: shouldUseShortMonthLabel ? 'short' : 'long'
              })
                .format(new Date(year, monthIndex))
              return monthName.charAt(0).toUpperCase() + monthName.slice(1)
            }
            return value
          }}
        />
        <YAxis
          tick={{ fontSize: isMobile ? 11 : 13, fill: COLOR_MUTED }} axisLine={false} tickLine={false}
          tickFormatter={(value) => {
            if (value >= 1_000_000) return (value / 1_000_000).toFixed(1) + 'M'
            if (value >= 1_000) return (value / 1_000).toFixed(0) + 'K'
            return value.toLocaleString('es-CL')
          }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend content={NoLegend} />
        <Line dataKey="Abono" stroke={COLOR_SUCCESS} strokeWidth={3} activeDot={{ r: 8 }} />
        <Line dataKey="Cargo" stroke={COLOR_DANGER} strokeWidth={3} activeDot={{ r: 8 }} />
      </LineChart>
    </ResponsiveContainer>
  )
}

// 3) Memo con comparador que ignora el callback (lo resolvemos con ref) y
//    solo re-renderiza si cambia el seleccionado o cambia realmente la data.
export default memo(SimpleBarChart, (prev, next) => {
  return (
    prev.selectedMonth === next.selectedMonth &&
    dataFingerprint(prev.data) === dataFingerprint(next.data)
  )
})
